import { CardDetails } from '@baemingo/adyen-terminal-comms';
import { sortBy, sumBy } from 'lodash';
import { DateTime } from 'luxon';

import { useReportsApi } from '~Components/ReportsUniverse';
import {
  ReceiptType,
  useApi,
  useDispatch,
  useI18n,
  useLogger,
  useReceiptGenerator,
  useSelector,
} from '~Hooks';
import { addToast } from '~actions';
import { useStorage } from '~Components/Storage';
import { PrintRow } from '~Components/PrinterManager';
import { receiptLinesToPrintRows } from '~Hooks/Entities/useReceipt';
import { ROLE, UserMetadataInput } from '../../__queries__';
import { GetOrder_order, GetOrder_order_user } from '~Scenes/History-v2/Components/__queries__';

export interface PrintReceiptArgs {
  type: ReceiptType;
  order: GetOrder_order;
  itemIds?: string[];
  cardDetails?: CardDetails;
  cardIdentifier?: string;
  cardMethod?: string;
  isCopy?: boolean;
  refundReceiptNumber?: number;
}

export const useReceiptCopy = () => {
  const [storage] = useStorage();
  const venueId = useSelector((state) => state.venueId);
  const flags = useSelector((state) => state.companyFlags);
  const country = useSelector((state) => state.country);

  const generateReceipt = useReceiptGenerator({ venueId: venueId || '' });
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const reportsApi = useReportsApi();
  const logger = useLogger('receipt-copy');
  const api = useApi();

  const mapItem = ({
    entry,
    price,
    fraction,
    variations,
    removedVariations,
    message,
  }: GetOrder_order['items'][0]) => ({
    entryId: entry.id,
    amount: price.amount,
    discount: price.discount,
    percentageVAT: price.percentageVAT,
    variations: variations?.map((variation) => i18n(variation.nameLang)),
    removedVariations: removedVariations?.map((variation) => i18n(variation.nameLang)),
    title: i18n(entry.item.titleLang),
    fraction,
    message,
  });

  const getReceiptItems = (order: GetOrder_order, itemIds?: string[]) =>
    order.items
      .filter((item) => !item.isRefunded)
      .filter((item) => {
        if (!itemIds?.length) return true;
        return itemIds.includes(item.id);
      })
      .map(mapItem);

  // Get refunded items for refund receipt copies
  const getRefundedItems = (
    order: GetOrder_order,
    itemIds?: string[],
    refundReceiptNumber?: number,
  ) => {
    // For a full order refund (no itemIds), exclude items that belong to
    // other refund receipts (individually refunded before the full refund)
    const itemIdsInOtherRefunds =
      !itemIds?.length && refundReceiptNumber !== undefined
        ? (order.refunds ?? [])
            .filter((r) => r.refundReceiptNumber !== refundReceiptNumber && r.itemId)
            .map((r) => r.itemId!)
        : [];

    return order.items
      .filter((item) => {
        if (itemIds?.length) return itemIds.includes(item.id);
        // Full order refund: exclude items that were refunded in a different receipt
        return !itemIdsInOtherRefunds.includes(item.id);
      })
      .map(mapItem);
  };

  const printReceiptCopy = async ({
    type,
    order,
    itemIds,
    isCopy,
    refundReceiptNumber,
  }: PrintReceiptArgs): Promise<void> => {
    const [
      _complianceId = undefined,
      _receiptCounter = undefined,
      deviceRegNumber,
      deviceId,
    ] = order.basketToken.split('-');

    // For refund receipts, only include the refunded items
    const items =
      type === ReceiptType.Refund
        ? getRefundedItems(order, itemIds, refundReceiptNumber)
        : getReceiptItems(order, itemIds);

    const amount = sumBy(items, (item) => item.amount - item.discount);
    const total = type === ReceiptType.Refund ? -amount : amount;
    const tip =
      type === ReceiptType.Refund
        ? -(order.payment.correctedTip ?? order.payment.tip)
        : order.payment.correctedTip ?? order.payment.tip;
    const totalDiscount = sumBy(items, (item) => item.discount);

    let userId: string | undefined = undefined;
    const isBackofficeAdmin =
      order?.user.role === ROLE.CONTENT_ADMIN || (order?.user.venues ?? []).length > 0;

    if (!isBackofficeAdmin && country === 'ES') {
      userId = order?.user?.id;
    }

    // Build extraVars depending on receipt type
    const isRefundCopy = type === ReceiptType.Refund;
    const isFullOrderRefund = isRefundCopy && !itemIds?.length;

    const isSpainWithNewLicenses = country === 'ES' && flags.useNewLicenses;

    const orderDate = order.createdAt
      ? isSpainWithNewLicenses
        ? DateTime.fromISO(order.createdAt).toFormat('MM/dd/yyyy')
        : DateTime.fromISO(order.createdAt).toFormat('yyyy-MM-dd HH:mm')
      : '';

    const extraVars: Record<string, string | undefined> = isRefundCopy
      ? {
          checkId: isSpainWithNewLicenses ? undefined : _receiptCounter,
          refundNumber:
            isSpainWithNewLicenses && refundReceiptNumber !== undefined
              ? order.posDevice.name + '-' + refundReceiptNumber
              : undefined,
          refundReference: isSpainWithNewLicenses
            ? order.posDevice.name + '-' + _receiptCounter + '  (' + orderDate + ')'
            : undefined,
          deviceName: storage.taxComplianceDeviceName || '',
          deviceId: storage.taxComplianceControlUnitSerial || '',
          orderDate: isSpainWithNewLicenses ? undefined : orderDate,
          easyId: (order.isTableOrder ? i18n('Receipt.Table') + ': ' : '#') + order.easyId,
          tip: isFullOrderRefund ? tip.toString() : undefined,
          paymentIncTip: isFullOrderRefund ? (total + tip).toFixed(2) : undefined,
        }
      : {
          tip: itemIds?.length ? undefined : tip.toString(),
          paymentIncTip: itemIds?.length ? undefined : (total + tip).toFixed(2),
          checkId: isSpainWithNewLicenses
            ? order.posDevice.name + '-' + _receiptCounter
            : _receiptCounter,
          itemsTotal: (total / 100).toFixed(2),
          deviceId,
          deviceRegNumber,
          datetime: order.createdAt,
          staffOnOrder: order.staff?.name || '',
        };

    const formattedItems = items.map((item) => ({
      ...item,
      title:
        item.message && item.message.includes('[cn]')
          ? item.message.replace('[cn] ', '')
          : item.title,
    }));

    // Sort items alphabetically by title
    const sortedItems = sortBy(formattedItems, (item) => item.title.toLowerCase());

    const receipt = await generateReceipt({
      items: sortedItems,
      totalDiscount,
      type,
      isCopy,
      cardDetails: order?.payment?.metadata?.cardDetails,
      payments: order.payment.subPayments.length
        ? order.payment.subPayments.map((payment) => ({
            type: payment.type,
            amount: (payment.amount / 100).toFixed(2),
            transactionId: payment.transactionId,
          }))
        : [
            {
              type: order.payment.type,
              amount: (total / 100).toFixed(2),
              transactionId: order.payment.transactionId,
            },
          ],
      extraVars,
      userId,
    });

    if (receipt) {
      const message: PrintRow[] = receiptLinesToPrintRows(receipt.lines);

      const QRContent =
        flags.hasPoints && !storage.printQROnCheque
          ? `claim:${order.payment.transactionId}:${total}:org_8AGDOueNZxuxTJ0PfaZk5`
          : undefined;

      if (QRContent) {
        message.push({ content: QRContent, qr: true });
      }

      window.printerManager?.initiatePrint({
        data: message,
        printType: 'RECEIPT',
        base64Image: receipt.base64Image,
        receipt: receipt.document,
      });

      // Increase receipt counter
      dispatch({
        type: 'INCREASE_RECEIPT_COUNTER',
      });

      const orderId = order.id;

      reportsApi.receiptCopyPrinted(amount);

      api.setOrderHasPrinted({ id: orderId, hasPrintedReceiptCopy: true });
    }
  };

  const emailReceiptCopy = async ({
    order,
    email,
    userConnectedToOrder,
  }: {
    order: GetOrder_order;
    email?: string;
    userConnectedToOrder?: GetOrder_order_user;
  }) => {
    let userMetadata: UserMetadataInput | undefined = undefined;

    /** This is spanish invoice customers */
    if (country === 'ES' && userConnectedToOrder?.orgNumber) {
      userMetadata = {
        id: userConnectedToOrder.id,
        address: userConnectedToOrder.address,
        city: userConnectedToOrder.city,
        orgNumber: userConnectedToOrder.orgNumber,
        zip: userConnectedToOrder.zip,
      };
    }

    const emailToUse = email || order.user.email;

    const result = await api.sendOrderEmail({
      orderId: order.id,
      email: emailToUse,
      userMetadata,
    });

    if (!result.success || result.data?.sendOrderEmail?.error) {
      logger.error('Error sending receipt copy email', {
        errorMessage: result.data?.sendOrderEmail?.message || result.error,
        orderId: order.id,
      });
      dispatch(addToast(i18n('TOAST.Email.Not.Sent'), true));
      return;
    }

    const amount = sumBy(order.items, (item) => item.price.amount - item.price.discount);

    reportsApi.receiptCopyEmailed(amount);

    dispatch(addToast(`${i18n('TOAST.Email.Sent')} ${emailToUse}`, true));

    api.setOrderHasPrinted({ id: order.id, hasPrintedReceiptCopy: true });
  };

  return { printReceiptCopy, emailReceiptCopy };
};
